import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { objectWithRandomUniqueKey } from '../../../common/utils/randomUniqueKey';
import { useAlgorithmFormValues } from '../useAlgorithmFormValues';
import { AlgorithmJoinItemSelectFieldOptionGroup } from './AlgorithmJoinItemSelectField';

/**
 * Finds first dimension option with code `SUBJEKT` in both left and right dimension options, and
 * if it is found on both sides and user has not selected any values yet, auto-selects them.
 * @param leftDimensionOptions
 * @param rightDimensionOptions
 * @param parentIndex Index of the parent join
 * @param count Number of join items in the parent join
 */

export const useAutoPairSubjectDimensions = (
  leftDimensionOptions: AlgorithmJoinItemSelectFieldOptionGroup[],
  rightDimensionOptions: AlgorithmJoinItemSelectFieldOptionGroup[],
  parentIndex: number,
  count: number,
) => {
  const [
    firstSourceDimension,
    firstTargetDimension,
    firstSourceValue,
    firstTargetValue,
  ] = useAlgorithmFormValues([
    `joins.${parentIndex}.algorithmJoinItems.0.sourceDimension`,
    `joins.${parentIndex}.algorithmJoinItems.0.targetDimension`,
    `joins.${parentIndex}.algorithmJoinItems.0.sourceValue`,
    `joins.${parentIndex}.algorithmJoinItems.0.targetValue`,
  ] as const);

  const firstSourceSelectedValue = firstSourceValue || firstSourceDimension;
  const firstTargetSelectedValue = firstTargetValue || firstTargetDimension;

  const { setValue } = useFormContext();

  return useCallback(() => {
    const firstSourceSubjectDimensionOption =
      findFirstSubjectDimension(leftDimensionOptions);
    const firstTargetSubjectDimensionOption = findFirstSubjectDimension(
      rightDimensionOptions,
    );

    if (
      !firstSourceSubjectDimensionOption ||
      !firstTargetSubjectDimensionOption
    ) {
      return;
    }

    const hasNoCustomJoinClauses =
      count === 1 &&
      (!firstSourceSelectedValue ||
        firstSourceSelectedValue === firstSourceSubjectDimensionOption) &&
      (!firstTargetSelectedValue ||
        firstTargetSelectedValue === firstTargetSubjectDimensionOption);

    if (hasNoCustomJoinClauses) {
      setValue(
        `joins.${parentIndex}.algorithmJoinItems.0`,
        objectWithRandomUniqueKey({
          _variant: 'DIMENSION_DIMENSION' as const,
          algorithmSourceId:
            firstSourceSubjectDimensionOption.algorithmSource.id,
          sourceDimension: firstSourceSubjectDimensionOption.dimension.code,
          targetDimension: firstTargetSubjectDimensionOption.dimension.code,
        }),
        { shouldDirty: true },
      );
    }
  }, [
    count,
    firstTargetSelectedValue,
    firstSourceSelectedValue,
    rightDimensionOptions,
    leftDimensionOptions,
    parentIndex,
    setValue,
  ]);
};

// Finding first SUBJEKT dimension value in options that are expected to be ordered by latest version first
const findFirstSubjectDimension = (
  options: AlgorithmJoinItemSelectFieldOptionGroup[],
) => {
  for (const optionGroup of options) {
    for (const option of optionGroup.options) {
      if ('dimension' in option && option.dimension.code === 'SUBJEKT') {
        return option;
      }
    }
  }
};
